/**
 * agacData — Landing "Riskmatik Ağacı" (3-çekirdek) verisi.
 * --------------------------------------------------------
 * İşlemci (Riskmatik) → 3 çekirdek (Görsel/Mevzuat/45001) → modül-yaprakları (devre kartı deseni).
 * Çekirdek + modül dağılımı manifest `cekirdek` alanının SSoT yansımasıdır
 * (documentation/UC_CEKIRDEK_MIMARI.md). Drift-guard: backend/tests/agac-cekirdek-drift.js
 * bu dosyadaki modül→çekirdek eşleşmesini manifestlerle karşılaştırır (sapma = kırmızı).
 *
 * Landing ağacı (SistemAgaci) TÜM yaprakları publicModules'tan render eder; çekirdek detay
 * sayfaları da aynı SSoT'tan üretilir. Yaprak linki = MODUL_PUBLIC_URL[id] (yoksa çekirdek sayfası).
 */
export interface AgacCekirdek {
  id: 'vision' | 'mevzuat' | '45001';
  ad: string;
  /** Fiil — GÖRÜR / ÜRETİR / YÖNETİR */
  fiil: string;
  /** Aksan rengi (cohesive ama ayırt edilebilir) */
  hue: string;
  /** Girdi etiketi (manifest girdiler özeti) */
  girdiler: string;
  /** Çekirdek detay sayfası */
  href: string;
  /** Tek-cümle ne yapar (GEO answer block) */
  ozet: string;
  /** Toplam modül sayısı (bu çekirdeğe ait) — guard: cekirdek-drift E (publicModules sayısıyla birebir) */
  modulSayisi: number;
}

/** Devre kartının İŞLEMCİSİ = Riskmatik (firma verisi tek yerde; 3 çekirdek buna bağlı). */
export const AGAC_RISKMATIK = {
  ad: 'Riskmatik',
  alt: 'firma · bölüm · çalışan',
  href: '/dijital-atolye',
};

/** Modül id → public detay sayfası slug'ı (SADECE canlı sayfası olanlar; yoksa yaprak çekirdek
 *  sayfasına iner — kırık link yok). Yeni modül-detay sayfası yapılınca buraya eklenir. */
export const MODUL_PUBLIC_URL: Record<string, string> = {
  'saha-gozlem': '/fotograftan-tehlike-tespiti',
  'ekipman-kontrol': '/ekipman-periyodik-kontrol-yazilimi',
  'risk-analizi': '/ai-destekli-risk-analizi',
  'acil-durum': '/acil-durum-plani-yazilimi', // FAZ 3 golden
  'yillik-planlar': '/yillik-calisma-plani-yazilimi', // FAZ 3 — YCP+YEP, şablon-driven (AI YOK)
  'egitim-sunum': '/isg-egitim-sunumu-yazilimi', // FAZ 3 — AI VAR (slayt+ölçme materyali), mevzuata grounded
  'duzeltici-faaliyetler': '/uygunsuzluk-yonetimi-yazilimi', // FAZ 3 — AI VAR (5-Whys kök neden), 10-kaynak hub, ISO 45001 Md 10.2
  'is-kazalari': '/is-kazasi-bildirim-yazilimi', // FAZ 3 — AI VAR (RCA 5-Why/Barrier/Tripod), SGK 3-gün takibi, 6331 Md 14
  'egitim-kayitlari': '/isg-egitim-kayitlari-yazilimi', // FAZ 3 — Resmî Gazete 33212 uyum motoru + 7 evrak + EK-2 + uzaktan izleme, AI dar (işe-özel konu)
  'isg-kurullari': '/isg-kurulu-yazilimi', // FAZ 3 — hub-toplantı, 6331 Md 22 (50+ çalışan), periyot+yeter-sayı, AI dar (dil)
  'calisanlar': '/calisan-yonetimi-yazilimi', // FAZ 3 — kişi-master, AI Excel/görsel import (extraction), 360° kart, KisiSecici kaynağı
  'taseronlar': '/taseron-evrak-takip-yazilimi', // 2026-10-17 — evrak talebi, hesapsız portal, onay, geçerlilik, Md 22 bilgilendirme (Resmî Gazete 33212)
  'is-izinleri': '/is-izni-yazilimi', // 2026-10-17 — 6 izin tipi, ön koşul denetimi, uzatma/devir, kapanış, form PDF
  'calisan-temsilcisi': '/calisan-temsilcisi-secimi-yazilimi', // 2026-09-11 — seçim/atama süreci, 10 resmî belge, öneri defteri (6331 Md 20 + Resmî Gazete 28750); prod 2026-09-21
  'onlem-takip': '/onlem-takip-yazilimi', // FAZ 3 — RA'dan türeyen kontrol önlemleri takibi (AI YOK bu modülde), kontrol hiyerarşisi Md 8.1.2
  'tatbikat-izleme': '/acil-durum-tatbikati-yazilimi', // FAZ 3 — AI senaryo + icra + ISO 8.2 değerlendirme, Resmî Gazete 28681 Md 13
  'kat-planlari': '/kacis-plani-yazilimi', // FAZ 3 — ISO 23601 kaçış planı (çiz/yükle), ISO 7010 sembol kütüphanesi, AI YOK
  'calisma-defteri': '/onayli-defter-yazilimi', // FAZ 3 — onaylı defter (İSG Hizmetleri Yön. Resmî Gazete 28512 Md 7), AI 7-modül agregasyon (İSG-KATİP DEĞİL)
  'yonetim-gozden-gecirme': '/yonetim-gozden-gecirme-yazilimi', // FAZ 3 — hub-toplantı YGG, ISO 45001 Md 9.3 (16 girdi+5 çıktı), AI YOK
  'calisan-katilimi': '/calisan-katilimi-proseduru-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 5.4 prosedür (IsoDocumentEditor), AI YOK
  'baglam-analizi': '/baglam-analizi-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 4.1/4.2 (SWOT/PESTEL şablon), AI YOK
  'kapsam-beyani': '/kapsam-beyani-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 4.3 (bağlama dayalı), AI YOK
  'isg-politikasi': '/isg-politikasi-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 5.2 (üst yönetim taahhüdü), AI YOK
  'roller-yetki': '/roller-yetki-matrisi-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 5.3 (RACI matrisi), AI YOK
  'liderlik-taahhut': '/liderlik-ve-taahhut-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 5.1 (üst yönetim beyanı), AI YOK
  'iletisim-plani': '/iletisim-plani-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 7.4 (iç/dış iletişim matrisi), AI YOK
  'degisiklik-yonetimi': '/degisiklik-yonetimi-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 8.1.3 (değişiklik prosedürü), AI YOK
  'yuklenici-yonetimi': '/yuklenici-yonetimi-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 8.1.4 (yüklenici, evrak eki), AI YOK
  'ic-denetim': '/ic-denetim-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 9.2 (iç denetim programı, STATİK), AI YOK
  'uyum-degerlendirme': '/uyum-degerlendirme-yazilimi', // FAZ 3 — 45001 tek-doküman, ISO 45001 Md 9.1.2 (uygunluk değerlendirme, STATİK), AI YOK
  'yetkinlik-matrisi': '/yetkinlik-matrisi-yazilimi', // FAZ 3 — 45001 DASHBOARD/pano, ISO 45001 Md 7.2 (yetkinlik denetimi), AI YOK
  'dokuman-merkezi': '/dokuman-merkezi-yazilimi', // FAZ 3 — 45001 DASHBOARD/hub, ISO 45001 Md 7.5 (12 doküman durumu), AI YOK
  'surekli-iyilestirme': '/surekli-iyilestirme-yazilimi', // FAZ 3 — 45001 DASHBOARD/KPI, ISO 45001 Md 10.3 (DF+sistem sağlık), AI YOK
};

/** 🔴 Modül-altında-modül: bir modül landing ağacında 2+ detay yüzeyine (alt-yaprak) dallanabilir.
 *  MODÜL SAYISI DEĞİŞMEZ (1 modül = 1 publicModule); yalnız landing dalı genişler. Örn Yıllık Planlar
 *  tek modül ama 2 belge (çalışma + eğitim) → 2 alt-yaprak. Guard alt-yaprak href'lerini de doğrular
 *  (backend/tests/modul-detay-drift.js). MODUL_PUBLIC_URL[id] = PRIMARY yüzey (manifest.publicDetayUrl). */
export const MODUL_ALT_YAPRAK: Record<string, { ad: string; href: string }[]> = {
  'yillik-planlar': [
    { ad: 'Yıllık Çalışma Planı', href: '/yillik-calisma-plani-yazilimi' },
    { ad: 'Yıllık Eğitim Planı', href: '/yillik-egitim-plani-yazilimi' },
  ],
};

export const AGAC_CEKIRDEKLER: AgacCekirdek[] = [
  {
    id: 'vision',
    ad: 'Görsel Yapay Zeka',
    fiil: 'GÖRÜR',
    hue: '#22d3ee',
    girdiler: 'görsel · video · ses',
    href: '/ai-vision',
    ozet: 'Fotoğraf, video ve sesten sahayı görür; tehlike, ekipman, bulgu ve riski tespit eder.',
    modulSayisi: 3,
  },
  {
    id: 'mevzuat',
    ad: 'Mevzuat Yapay Zekası',
    fiil: 'ÜRETİR',
    hue: '#a78bfa',
    girdiler: 'metin · form · test',
    href: '/ai-mevzuat',
    ozet: 'Mevzuattan üretir ve yönetir; plan, eğitim, kurul, önlem takip, kayıt ve toplantı — günlük kullanılan araçlar, uzman onaylar.',
    modulSayisi: 16,
  },
  {
    id: '45001',
    ad: '45001 Yapay Zekası',
    fiil: 'YÖNETİR',
    hue: '#34d399',
    girdiler: 'form · kayıt',
    href: '/iso-45001-yazilimi',
    ozet: 'ISO 45001 çerçeve dokümanlarını yönetir; ISO 45001 yol haritasındaki politika, bağlam, roller, kayıt ve denetim modülleri.',
    modulSayisi: 14,
  },
];
